// Referral attribution cookie. Dropped at `/r/:code` (vanity click) and read at
// signup so the `register` mutation can carry `refCode`/`refClickedAt`.
//
// Scope decision (Phase 1, localhost): HOST-ONLY — no `domain=` attribute. The
// `os_utm` cookie (utm-capture.ts) is `.optisigns.com`-scoped by Webflow GTM; we
// deliberately do NOT copy that domain, since a `.optisigns.com` domain breaks
// `localhost`. Non-HttpOnly is required (JS reads it at signup) — flagged for a
// later security review. 30-day lifetime. We only mirror the `document.cookie`
// read/parse pattern from utm-capture.ts.

const CODE_COOKIE = 'os_ref';
const CLICKED_AT_COOKIE = 'os_ref_at';
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

// Referral codes are short, URL-safe tokens. Anything else (oversized, reserved
// chars, empty) is hostile/garbage and treated as ABSENT — never stored, never
// echoed into a redirect URL, never returned to the register mutation.
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

/** True when `code` is a well-formed referral code (URL-safe, 1–32 chars). */
export function isValidReferralCode(code: string | undefined): code is string {
  return code !== undefined && CODE_PATTERN.test(code);
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

function writeCookie(name: string, value: string): void {
  // Host-only (no domain=), non-HttpOnly, 30-day, SameSite=Lax, path=/.
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${THIRTY_DAYS_SECONDS}; Path=/; SameSite=Lax`;
}

function expireCookie(name: string): void {
  // Same host-only/Path/SameSite attributes as writeCookie so the browser
  // targets the exact same cookie; Max-Age=0 deletes it immediately.
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/**
 * Drop the referral cookie at vanity-link click time. Stores the code plus a
 * unix-seconds click timestamp (used by the engine for most-recent-wins ties).
 */
export function setReferralCookie(code: string): void {
  const clickedAt = Math.floor(Date.now() / 1000);
  writeCookie(CODE_COOKIE, code);
  writeCookie(CLICKED_AT_COOKIE, String(clickedAt));
}

/**
 * The referral code captured at click time, or undefined if none. A stored
 * value that fails validation (oversized/garbage) is treated as absent so a
 * tampered cookie can't ride into the register mutation.
 */
export function getReferralCode(): string | undefined {
  const code = readCookie(CODE_COOKIE);
  return isValidReferralCode(code) ? code : undefined;
}

/** Unix-seconds click timestamp, or undefined if absent/non-numeric. */
export function getReferralClickedAt(): number | undefined {
  const raw = readCookie(CLICKED_AT_COOKIE);
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Clear the referral attribution cookies after a successful signup, so a second
 * signup in the same browser (within the 30-day window) doesn't ride a stale
 * `refCode`. Attribution is one-shot: consumed at register time, then dropped.
 */
export function clearReferralCookie(): void {
  expireCookie(CODE_COOKIE);
  expireCookie(CLICKED_AT_COOKIE);
}
